import type { AccountSummary, Provider } from '@fruitful/types';
import { useAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    CheckIcon,
    Icon,
    isWeb,
    KeyboardCompatibleBottomSheet,
    UnlinkIcon,
    useToastController,
    View,
} from 'smoothie';

import { captureException, captureMessage } from '../../../logger/Logger';
import useDeleteGoalAccountLink from '../../hooks/useDeleteGoalAccountLink';
import useGetGoals from '../../hooks/useGetGoals';
import useUpdateGoalAccountLink from '../../hooks/useUpdateGoalAccountLink';
import { selectedGoalAtom } from '../../state/GoalAtoms';
import { ACCOUNT_LINKING_TOAST_DELAY } from '../../types/constants';
import type { IGoal } from '../../types/Goal';
import AccountList from './AccountList';
import LinkedAccountDetails from './LinkedAccountDetails';

interface EditGoalAccountLinkSheetProps {
    goalAccountLinkId: string;
    linkedAccount: AccountSummary;
    onDismiss: () => void;
}

const EditGoalAccountLinkSheet = ({
    goalAccountLinkId,
    linkedAccount,
    onDismiss,
}: EditGoalAccountLinkSheetProps) => {
    const toast = useToastController();
    const [selectedGoal, setSelectedGoal] = useAtom(selectedGoalAtom);
    const [showSelectAccount, setShowSelectAccount] = useState(false);

    const { refetch: refetchGoals } = useGetGoals();

    const { mutate: updateGoalAccountLink } = useUpdateGoalAccountLink();
    const { mutate: deleteGoalAccountLink } = useDeleteGoalAccountLink();

    const onUpdate = (accountSummary: AccountSummary) => {
        const { id: accountId, provider } = accountSummary;

        updateGoalAccountLink(
            {
                body: {
                    accountId,
                    provider: provider.toUpperCase() as Provider,
                },
                urlParams: {
                    goalAccountLinkId,
                },
            },
            {
                onSuccess(data) {
                    // update the goal details page UI immediately
                    const updatedGoal = {
                        ...(selectedGoal as IGoal),
                        accountLinks: [data],
                    };
                    setSelectedGoal(updatedGoal);
                    void refetchGoals();

                    onDismiss();

                    setTimeout(
                        () =>
                            toast.show('Account linked', {
                                duration: 2000,
                                description: `Your linked account has been updated`,
                                icon: (
                                    <View
                                        width={48}
                                        height={48}
                                        alignItems="center"
                                        justifyContent="center"
                                        backgroundColor="$green1000"
                                        borderRadius={8}
                                    >
                                        <Icon
                                            icon={CheckIcon}
                                            size={24}
                                            color="$white"
                                        />
                                    </View>
                                ),
                            }),
                        ACCOUNT_LINKING_TOAST_DELAY,
                    );
                },
                onError(e) {
                    // TODO: show toast?
                    captureMessage('Error updating goal account link');
                    captureException(e, {
                        accountId,
                        provider,
                    });
                },
            },
        );
    };

    const onUnlink = () => {
        deleteGoalAccountLink(
            {
                urlParams: {
                    goalAccountLinkId,
                },
            },
            {
                onSuccess() {
                    // update the goal details page UI immediately
                    const updatedGoal = {
                        ...(selectedGoal as IGoal),
                        accountLinks: [],
                    };
                    setSelectedGoal(updatedGoal);
                    void refetchGoals();

                    onDismiss();

                    setTimeout(
                        () =>
                            toast.show('Unlinked', {
                                duration: 2000,
                                description: 'Account unlinked from your goal',
                                icon: (
                                    <View
                                        width={48}
                                        height={48}
                                        alignItems="center"
                                        justifyContent="center"
                                        backgroundColor="$red900"
                                        borderRadius={8}
                                    >
                                        <Icon
                                            icon={UnlinkIcon}
                                            size={16}
                                            color="$white"
                                        />
                                    </View>
                                ),
                            }),
                        ACCOUNT_LINKING_TOAST_DELAY,
                    );
                },
                onError(e) {
                    // TODO: show toast?
                    captureMessage('Error deleting goal account link');
                    captureException(e, {
                        goalAccountLinkId,
                    });
                },
            },
        );
    };

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onDismiss}
            enablePanDownToClose={!isWeb}
            enableContentPanningGesture={!isWeb}
            showCloseButton={false}
        >
            {showSelectAccount ? (
                <AccountList
                    onSelectAccount={(accountSummary) => {
                        onUpdate(accountSummary);
                    }}
                    onDismiss={onDismiss}
                    onBack={() => {
                        setShowSelectAccount(false);
                    }}
                />
            ) : (
                <LinkedAccountDetails
                    linkedAccount={linkedAccount}
                    onEdit={() => {
                        setShowSelectAccount(true);
                    }}
                    onUnlink={onUnlink}
                    onDismiss={onDismiss}
                />
            )}
        </KeyboardCompatibleBottomSheet>
    );
};

export default EditGoalAccountLinkSheet;
